import React, { useState, useMemo, useCallback } from 'react';
import { View, Text, TouchableOpacity, Modal } from 'react-native';
import { ReportType } from '../types';
import colors from '@constants/color';
import { ScaledSheet } from '@utils/scale';

interface DateRangeOption {
  key: string;
  value: string;
}

interface DatePickerProps {
  dateRangeOptions: DateRangeOption[];
  dateRangeText: string;
  selectedDate: DateRangeOption;
  onDateChange: (date: DateRangeOption) => void;
  reportType: ReportType;
}

interface WeekOptionItemProps {
  option: DateRangeOption;
  isSelected: boolean;
  onSelect: (option: DateRangeOption) => void;
}

// Constants
const HIT_SLOP = { top: 10, bottom: 10, left: 10, right: 10 };
const MODAL_OPACITY = 0.7;

/**
 * WeekOptionItem Component
 * Individual week option item in the modal picker
 */
const WeekOptionItem: React.FC<WeekOptionItemProps> = React.memo(({ option, isSelected, onSelect }) => {
  const handlePress = useCallback(() => {
    onSelect(option);
  }, [option, onSelect]);

  return (
    <TouchableOpacity
      style={[styles.weekOption, isSelected && styles.weekOptionSelected]}
      onPress={handlePress}
      activeOpacity={MODAL_OPACITY}
    >
      <View style={styles.radioButton}>
        {isSelected && <View style={styles.radioButtonInner} />}
      </View>
      <Text style={[styles.weekOptionText, isSelected && styles.weekOptionTextSelected]}>
        {option.value}
      </Text>
    </TouchableOpacity>
  );
});

WeekOptionItem.displayName = 'WeekOptionItem';

/**
 * WeeklyDatePicker Component
 * Allows navigation between weeks and week selection via modal
 */
const WeeklyDatePicker: React.FC<DatePickerProps> = ({
  dateRangeOptions = [],
  dateRangeText,
  selectedDate,
  onDateChange,
}) => {
  const [showWeekPicker, setShowWeekPicker] = useState(false);

  // Memoize current index to avoid recalculating on every render
  const currentIndex = useMemo(() => {
    if (!dateRangeOptions.length || !selectedDate?.key) return -1;
    return dateRangeOptions.findIndex((option) => option.key === selectedDate.key);
  }, [dateRangeOptions, selectedDate?.key]);

  // Check if navigation is possible
  const  canNavigateNext = useMemo(() => {
    return currentIndex >= 0 && currentIndex < dateRangeOptions.length - 1;
  }, [currentIndex, dateRangeOptions.length]);

  const canNavigatePrevious = useMemo(() => {
    return currentIndex > 0;
  }, [currentIndex]);

  // Navigate to older week (higher index since options are ordered newest to oldest)
  const   handleNextDate= useCallback(() => {
    if (currentIndex < 0 || currentIndex >= dateRangeOptions.length - 1) return;
    const olderWeekIndex = currentIndex + 1;
    onDateChange(dateRangeOptions[olderWeekIndex]);
  }, [currentIndex, dateRangeOptions, onDateChange]);

  // Navigate to newer week (lower index since options are ordered newest to oldest)
  const handlePreviousDate = useCallback(() => {
    if (currentIndex <= 0) return;
    const newerWeekIndex = currentIndex - 1;
    onDateChange(dateRangeOptions[newerWeekIndex]);
  }, [currentIndex, dateRangeOptions, onDateChange]);

  const handleOpenPicker = useCallback(() => {
    setShowWeekPicker(true);
  }, []);

  const handleClosePicker = useCallback(() => {
    setShowWeekPicker(false);
  }, []);

  const handleWeekSelect = useCallback((week: DateRangeOption) => {
    onDateChange(week);
    setShowWeekPicker(false);
  }, [onDateChange]);

  return (
    <>
      <View style={styles.container}>
        <TouchableOpacity
          style={styles.arrowButton}
          onPress={handlePreviousDate}
          hitSlop={HIT_SLOP}
          disabled={!canNavigatePrevious}
        >
          <Text style={[styles.arrowText, !canNavigatePrevious && styles.arrowTextDisabled]}>
            ‹
          </Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.dateContainer} onPress={handleOpenPicker}>
          <Text style={styles.dateText}>{dateRangeText}</Text>
          <Text style={styles.dropdownIcon}>▼</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.arrowButton}
          onPress={handleNextDate}
          hitSlop={HIT_SLOP}
          disabled={!canNavigateNext}
        >
          <Text style={[styles.arrowText, !canNavigateNext && styles.arrowTextDisabled]}>
            ›
          </Text>
        </TouchableOpacity>
      </View>

      <Modal
        statusBarTranslucent
        navigationBarTranslucent
        visible={showWeekPicker}
        transparent
        animationType="fade"
        onRequestClose={handleClosePicker}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={handleClosePicker}
        >
          <View style={styles.weekPickerContainer} onStartShouldSetResponder={() => true}>
            <Text style={styles.modalTitle}>Select Week</Text>
            <View style={styles.weekGrid}>
              {dateRangeOptions.map((option) => {
                const isSelected = option.key === selectedDate?.key;
                return (
                  <WeekOptionItem
                    key={option.key}
                    option={option}
                    isSelected={isSelected}
                    onSelect={handleWeekSelect}
                  />
                );
              })}
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const styles = ScaledSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  arrowButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  arrowText: {
    fontSize: 28,
    color: '#000000',
    fontWeight: '300',
  },
  arrowTextDisabled: {
    color: '#CCCCCC',
    opacity: 0.5,
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EAEBEC',
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 10,
    marginHorizontal: 8,
    gap: 8,
  },
  dateText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#000000',
  },
  dropdownIcon: {
    fontSize: 10,
    color: '#666666',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  weekPickerContainer: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 20,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 16,
    textAlign: 'left',
  },
  weekGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  weekOption: {
    width: '48%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: '#F5F5F5',
    borderRadius: 20,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  weekOptionSelected: {
    backgroundColor: colors.white,
    borderColor: '#000000',
  },
  radioButton: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: '#BDBDBD',
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#000000',
  },
  weekOptionText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#000000',
    flex: 1,
  },
  weekOptionTextSelected: {
    color: '#000000',
    fontWeight: '600',
  },
});

export default WeeklyDatePicker;


